// src/components/OutlookMail/OutlookMailProcessing.tsx
// Processing indicator for the Outlook mail tool.
// Follows the standard tool processing pattern: [icon] [status text] [animated dots]

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import OutlookLogo from "../../../assets/Outlook_Logo.svg";

const OUTLOOK_MESSAGES = [
  "Drafting your email...",
  "Composing the summary...",
  "Refining the content...",
  "Preparing email preview...",
  "Connecting to Outlook...",
  "Almost ready...",
];

const MESSAGE_ROTATION_MS = 3000;

interface OutlookMailProcessingProps {
  isProcessingTool: boolean;
  startTime?: number;
}

const OutlookMailProcessing: React.FC<OutlookMailProcessingProps> = ({ isProcessingTool }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!isProcessingTool) {
      setMessageIndex(0);
      return;
    }

    intervalRef.current = setInterval(() => {
      setMessageIndex((prev) => {
        const next = prev + 1;
        return next < OUTLOOK_MESSAGES.length ? next : prev; // stop at last message
      });
    }, MESSAGE_ROTATION_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isProcessingTool]);

  if (!isProcessingTool) return null;

  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 12px",
          backgroundColor: "#F8F9FA",
          borderRadius: "8px",
          border: "1px solid #E9ECEF",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <OutlookLogo style={{ width: 22, height: 22, flexShrink: 0, overflow: "visible" }} />

        <span
          style={{
            fontSize: "13px",
            color: "#495057",
            fontWeight: 500,
          }}
        >
          {OUTLOOK_MESSAGES[messageIndex]}
        </span>

        {/* Animated dots — same pattern as Catylex/SharePoint */}
        <svg width="20" height="6" viewBox="0 0 20 6" style={{ flexShrink: 0 }}>
          <style>
            {`
              @keyframes outlook-dot {
                0%, 80%, 100% { opacity: 0.2; }
                40% { opacity: 1; }
              }
              .outlook-dot-1 { animation: outlook-dot 1.4s ease-in-out infinite; }
              .outlook-dot-2 { animation: outlook-dot 1.4s ease-in-out infinite 0.2s; }
              .outlook-dot-3 { animation: outlook-dot 1.4s ease-in-out infinite 0.4s; }
            `}
          </style>
          <circle className="outlook-dot-1" cx="3" cy="3" r="2.5" fill="#0078D4" />
          <circle className="outlook-dot-2" cx="10" cy="3" r="2.5" fill="#0078D4" />
          <circle className="outlook-dot-3" cx="17" cy="3" r="2.5" fill="#0078D4" />
        </svg>
      </div>
    </div>
  );
};

export default OutlookMailProcessing;
